import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import {
  LiveAvatarSession,
  SessionState,
  SessionEvent,
  ConnectionQuality,
  AgentEventsEnum,
  SessionDisconnectReason,
} from "@heygen/liveavatar-web-sdk";

interface LiveAvatarProps {
  sessionToken: string | null;
  onReady?: (sessionId: string) => void;
  onError?: (error: Error) => void;
  onAvatarTalkingChange?: (isTalking: boolean) => void;
}

export interface LiveAvatarRef {
  repeatAudio: (audioBase64: string) => void;
  stopSession: () => Promise<void>;
  getSessionState: () => SessionState | null;
  /** POST /v1/sessions/keep-alive — call on an interval while idle to avoid server/WebSocket timeout */
  keepAlive: () => Promise<void>;
  /** Server max session length in seconds, if known after start(); otherwise null */
  getMaxSessionDuration: () => number | null;
}

// Store session outside component to persist across StrictMode remounts
const sessionCache = new Map<string, LiveAvatarSession>();

const LiveAvatar = forwardRef<LiveAvatarRef, LiveAvatarProps>(({
  sessionToken,
  onReady,
  onError,
  onAvatarTalkingChange,
}, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sessionRef = useRef<LiveAvatarSession | null>(null);
  const [sessionState, setSessionState] = useState<SessionState>(SessionState.INACTIVE);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(ConnectionQuality.UNKNOWN);
  const initializationStateRef = useRef<'idle' | 'initializing' | 'initialized' | 'error'>('idle');
  const lastTokenRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Store callbacks in refs to avoid dependency issues
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onAvatarTalkingChangeRef = useRef(onAvatarTalkingChange);
  
  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
    onAvatarTalkingChangeRef.current = onAvatarTalkingChange;
  }, [onReady, onError, onAvatarTalkingChange]);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Ensure video element is not muted and volume is set
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.volume = 1.0;
    }
  }, []);

  useImperativeHandle(ref, () => ({
    repeatAudio: (audioBase64: string) => {
      const session = sessionRef.current;
      if (session?.state === SessionState.CONNECTED) {
        try {
          session.repeatAudio(audioBase64);
        } catch (error) {
          console.error("Error sending audio to LiveAvatar:", error);
          throw error;
        }
      } else {
        console.warn("Cannot send audio - session not connected. State:", session?.state);
      }
    },
    stopSession: async () => {
      if (sessionRef.current) {
        await sessionRef.current.stop();
      }
    },
    getSessionState: () => sessionRef.current?.state ?? null,
    keepAlive: async () => {
      const session = sessionRef.current;
      if (session?.state !== SessionState.CONNECTED) {
        return;
      }
      try {
        await session.keepAlive();
      } catch (error) {
        console.error("LiveAvatar keepAlive failed:", error);
        throw error;
      }
    },
    getMaxSessionDuration: () => sessionRef.current?.maxSessionDuration ?? null,
  }), []);

  useEffect(() => {
    if (!sessionToken) {
      return;
    }

    // Cancel any pending cleanup
    if (cleanupTimeoutRef.current) {
      console.log("Cancelling pending cleanup - component remounted");
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    // Check if we already have a session for this token
    const existingSession = sessionCache.get(sessionToken);
    if (existingSession) {
      console.log("Reusing existing session for token");
      sessionRef.current = existingSession;
      initializationStateRef.current = 'initialized';
      lastTokenRef.current = sessionToken;
      
      // Re-attach video element if stream is ready
      if (existingSession.state === SessionState.CONNECTED && videoRef.current) {
        existingSession.attach(videoRef.current);
        setSessionState(SessionState.CONNECTED);
        setIsStreamReady(true);
        if (onReadyRef.current) {
          onReadyRef.current(sessionToken);
        }
      } else {
        setSessionState(existingSession.state);
      }
      return;
    }

    // Prevent duplicate initializations for the same token
    if (lastTokenRef.current === sessionToken) {
      if (initializationStateRef.current === 'initializing' || 
          initializationStateRef.current === 'initialized') {
        console.log("Skipping duplicate initialization for same token");
        return;
      }
    }

    // Prevent concurrent initializations
    if (initializationStateRef.current === 'initializing') {
      console.log("Skipping - already initializing");
      return;
    }

    console.log("Starting LiveAvatar initialization with token");
    initializationStateRef.current = 'initializing';
    lastTokenRef.current = sessionToken;

    const initializeSession = async () => {
      try {
        // Create LiveAvatarSession with the session token
        const session = new LiveAvatarSession(sessionToken, {
          voiceChat: false, // We're using custom audio, not voice chat
        });

        // Check if we should abort (component unmounted or token changed)
        if (!isMountedRef.current || lastTokenRef.current !== sessionToken) {
          console.log("Aborting initialization - component unmounted or token changed");
          return;
        }

        sessionRef.current = session;
        sessionCache.set(sessionToken, session); // Cache the session

        // Set up event listeners
        session.on(SessionEvent.SESSION_STATE_CHANGED, (state: SessionState) => {
          if (!isMountedRef.current) return;
          console.log("LiveAvatar session state changed:", state);
          setSessionState(state);
          
          if (state === SessionState.DISCONNECTED) {
            session.removeAllListeners();
            setIsStreamReady(false);
            initializationStateRef.current = 'idle';
          }
        });

        session.on(SessionEvent.SESSION_STREAM_READY, () => {
          if (!isMountedRef.current) return;
          console.log("LiveAvatar stream ready");
          setIsStreamReady(true);
          
          // Attach video element when stream is ready
          if (videoRef.current) {
            session.attach(videoRef.current);
            videoRef.current.muted = false;
            videoRef.current.volume = 1.0;
          }
          
          // Call onReady callback
          if (onReadyRef.current) {
            onReadyRef.current(sessionToken);
          }
        });

        session.on(SessionEvent.SESSION_CONNECTION_QUALITY_CHANGED, (quality: ConnectionQuality) => {
          if (!isMountedRef.current) return;
          console.log("Connection quality changed:", quality);
          setConnectionQuality(quality);
        });

        // Session disconnected event - important for debugging
        session.on(SessionEvent.SESSION_DISCONNECTED, (reason: SessionDisconnectReason) => {
          console.error("LiveAvatar session disconnected. Reason:", reason);
          if (isMountedRef.current) {
            initializationStateRef.current = 'error';
            if (onErrorRef.current) {
              onErrorRef.current(new Error(`Session disconnected: ${reason}`));
            }
          }
        });

        // Avatar talking events
        session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
          if (!isMountedRef.current) return;
          console.log("Avatar started speaking");
          if (onAvatarTalkingChangeRef.current) {
            onAvatarTalkingChangeRef.current(true);
          }
        });

        session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
          if (!isMountedRef.current) return;
          console.log("Avatar stopped speaking");
          if (onAvatarTalkingChangeRef.current) {
            onAvatarTalkingChangeRef.current(false);
          }
        });

        // Check again before starting
        if (!isMountedRef.current || lastTokenRef.current !== sessionToken) {
          console.log("Aborting before start - component unmounted or token changed");
          return;
        }

        // Start the session
        console.log("Calling session.start()...");
        console.log("Current session state before start:", session.state);
        
        await session.start();
        
        if (!isMountedRef.current) return;
        
        console.log("LiveAvatar session.start() completed");
        console.log("Current session state after start:", session.state);
        initializationStateRef.current = 'initialized';
      } catch (error) {
        console.error("Error initializing LiveAvatar:", error);
        if (isMountedRef.current) {
          initializationStateRef.current = 'error';
          if (onErrorRef.current) {
            onErrorRef.current(error as Error);
          }
        }
      }
    };

    initializeSession();

    // Cleanup function - delayed to allow for StrictMode remounts
    return () => {
      console.log("LiveAvatar cleanup triggered - scheduling delayed cleanup");
      
      // Delay the actual cleanup to allow StrictMode remount to cancel it
      cleanupTimeoutRef.current = setTimeout(() => {
        console.log("Executing delayed cleanup");
        
        if (sessionRef.current) {
          try {
            sessionRef.current.removeAllListeners();
            sessionRef.current.stop().catch((err) => {
              console.error("Error stopping LiveAvatar session:", err);
            });
            // Remove from cache
            if (sessionToken) {
              sessionCache.delete(sessionToken);
            }
          } catch (error) {
            console.error("Error during LiveAvatar cleanup:", error);
          }
        }
        
        sessionRef.current = null;
        initializationStateRef.current = 'idle';
        lastTokenRef.current = null;
        cleanupTimeoutRef.current = null;
      }, 100); // 100ms delay - StrictMode remounts happen synchronously
    };
  }, [sessionToken]);

  // Attach video element when it becomes available and stream is ready
  useEffect(() => {
    if (isStreamReady && videoRef.current && sessionRef.current) {
      sessionRef.current.attach(videoRef.current);
      videoRef.current.muted = false;
      videoRef.current.volume = 1.0;
    }
  }, [isStreamReady]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        backgroundColor: "#000"
      }}
    >
      <video
        ref={videoRef}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: "translateX(1.5%) translateY(1.5%) scale(1.05)"
        }}
        autoPlay
        muted={false}
        playsInline
      />
      {/* Connection status indicator */}
      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          padding: "4px 8px",
          borderRadius: 4,
          backgroundColor: "rgba(0,0,0,0.5)",
          color: "#fff",
          fontSize: 11,
          display: sessionState !== SessionState.CONNECTED ? "block" : "none",
        }}
      >
        {sessionState === SessionState.CONNECTING && "Connecting to avatar..."}
        {sessionState === SessionState.INACTIVE && "Starting session..."}
        {sessionState === SessionState.DISCONNECTING && "Disconnecting..."}
        {sessionState === SessionState.DISCONNECTED && "Disconnected - refresh to retry"}
      </div>
    </div>
  );
});

LiveAvatar.displayName = "LiveAvatar";

export default React.memo(LiveAvatar, (prevProps, nextProps) => {
  return (
    prevProps.sessionToken === nextProps.sessionToken &&
    prevProps.onReady === nextProps.onReady &&
    prevProps.onError === nextProps.onError &&
    prevProps.onAvatarTalkingChange === nextProps.onAvatarTalkingChange
  );
});
